import { DataAction, StateRepository } from '@angular-ru/ngxs/decorators'
import { catchError, map, switchMap } from 'rxjs/operators'
import { from, Observable, of } from 'rxjs'
import { State } from '@ngxs/store'
import { Injectable, inject } from '@angular/core'
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories'
import * as Apollo from 'apollo-angular'
import { ApolloQueryResult } from '@apollo/client'
import {
  SimpleFilterExternalState,
  SimpleFilterExternalStateModel,
} from './simple-filter-external.state'
import { DropdownOption, MatTablePaginationDirection } from '@wm-front/core'
import { GqlPageInfos, PaginationArgs } from '@wm-front/shared/interfaces'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FilterValues } from '@wm-front/shared/routed-filters-service'
import { getPaginationArgs } from '@wm-front/shared/utils'
import { mergeArrays } from '../helpers/device-options.utils'

export interface SimpleFilterQueryState {
  loading?: boolean
  error?: Error | null
  data?: DropdownOption[]
  pageInfos?: GqlPageInfos
}

export interface SimpleFilterStateModel {
  [key: string]: SimpleFilterQueryState
}

@StateRepository()
@State({
  name: 'simpleFilterState',
  defaults: {},
})
@Injectable()
export class SimpleFilterState extends NgxsDataRepository<SimpleFilterStateModel> {
  public externalState = inject(SimpleFilterExternalState)

  /**
   * It will update options as selected
   * @param {string} key
   * @param {number[] | string[]} values
   */
  @DataAction()
  public updateSelected(key: string, values: number[] | string[]): void {
    const state = this.getState()
    const data = state[key] ? state[key].data : []
    const clone = data?.map((d) => ({ ...d }))
    clone?.forEach((d) => {
      if (!d.children?.length) {
        d.checked = (values as string[])?.includes(d.value as string)
      } else {
        d.children = d.children.map((child) => ({ ...child }))
        d.children.forEach(
          (child) =>
            (child.checked = (values as string[])?.includes(
              child.value as string
            ))
        )
      }
    })
    this.setDataFromKey(key, 'data', clone)
  }

  /**
   * Will return the state of the data
   * @param {string} key
   * @returns {Observable<DropdownOption[] | undefined>}
   */
  public data$(key: string): Observable<DropdownOption[] | undefined> {
    return this.state$.pipe(
      map((state) => {
        return state && state[key] ? state[key].data : []
      })
    )
  }

  /**
   * Will return the state of the loading
   * @param {string} key
   * @returns {Observable<boolean>}
   */
  public loading$(key: string): Observable<boolean> {
    return this.state$.pipe(map((state) => !!state[key].loading))
  }

  /**
   * Will return the state of the error
   * @param {string} key
   * @returns {Observable<Error | null | undefined>}
   */
  public error$(key: string): Observable<Error | null | undefined> {
    return this.state$.pipe(map((state) => state[key].error))
  }

  /**
   * Will return the state of the pagination
   * @param {string} key
   * @returns {GqlPageInfos | undefined}
   */
  public pagination(key: string): GqlPageInfos | undefined {
    return this.getState()[key]?.pageInfos
  }

  /**
   * Will fetch the data from the provided query, and will set any filters and pagination necessary
   * @param {string} key
   * @param {Query<any, any>} query
   * @param {(data: ApolloQueryResult<any>) => DropdownOption[]} dropdownOptionConverter
   * @param {(data: ApolloQueryResult<any>) => GqlPageInfos} paginationConverter
   * @param {boolean} reset
   * @param bodyConverter
   * @param filters
   * @param {number} pageSize
   * @param search an additional filter coming from the component filter itself
   * @param fetchSelectedDataBody
   * @returns {Observable<void> | Observable<never>}
   */
  public fetch(
    key: string,
    query: Apollo.Query<any, any>,
    dropdownOptionConverter: (data: ApolloQueryResult<any>) => DropdownOption[],
    paginationConverter: (
      data: ApolloQueryResult<any>,
      state?: SimpleFilterStateModel
    ) => GqlPageInfos,
    reset = true,
    bodyConverter?: (
      filters: any,
      state: SimpleFilterStateModel,
      externalState: SimpleFilterExternalStateModel,
      pagination?: GqlPageInfos,
      reset?: boolean,
      paginationToFetchDataWith?: PaginationArgs
    ) => any,
    filters?: Partial<FilterValues>,
    pageSize?: number,
    search?: string,
    fetchSelectedDataBody?: Promise<any>
  ) {
    if (!reset && this.pagination(key) && !this.pagination(key)?.hasNextPage) {
      return of()
    }
    const pagination = this.pagination(key)
      ? getPaginationArgs(
          MatTablePaginationDirection.NEXT,
          pageSize || 10,
          this.pagination(key) as GqlPageInfos,
          reset
        )
      : { first: pageSize || 10 }
    if (search) {
      filters = { ...filters, search } // The search keyword may not be the right one, to improve in the future
    }
    this.setLoading(key, true)
    this.setError(key, null)
    return query
      .fetch(
        !bodyConverter
          ? { filters, pagination }
          : bodyConverter(
              filters,
              this.getState(),
              this.externalState.getState(),
              this.pagination(key),
              reset,
              pagination || undefined
            ),
        {
          fetchPolicy: 'no-cache',
        }
      )
      .pipe(
        switchMap((res) => {
          if (reset && fetchSelectedDataBody) {
            return from(fetchSelectedDataBody).pipe(
              switchMap((body) => {
                if (body) {
                  return query.fetch(body).pipe(
                    map((selected) => {
                      const selectedOptions = dropdownOptionConverter(selected)
                      return {
                        options: [
                          ...selectedOptions,
                          ...this.removeWhatsAlreadySelected(
                            dropdownOptionConverter(res),
                            selectedOptions
                          ),
                        ],
                        res,
                      }
                    })
                  )
                } else {
                  return of({ options: dropdownOptionConverter(res), res })
                }
              })
            )
          }
          return of({ options: dropdownOptionConverter(res), res })
        }),
        map(({ options, res }) => {
          const state = this.getState()
          let data = options
          data =
            state[key]?.data && !reset
              ? mergeArrays(state[key].data || [], data)
              : data
          this.patchState({
            ...state,
            [key]: {
              ...state[key],
              data,
              pageInfos: paginationConverter?.(res, this.getState()),
            },
          })
          this.setLoading(key, false)
        }),
        catchError((error) => {
          console.error(error)
          this.setLoading(key, false)
          this.setError(key, error)
          return of(error)
        })
      )
  }

  private removeWhatsAlreadySelected(
    options: DropdownOption[],
    alreadySelectedOptions: DropdownOption[]
  ): DropdownOption[] {
    if (options.some((o) => o.children?.length)) {
      return options.filter((o) =>
        alreadySelectedOptions.every(
          (selectedOption) => o.value !== selectedOption.value
        )
      )
    }
    return options.filter((o) =>
      alreadySelectedOptions.every(
        (selectedOption) => JSON.stringify(o) !== JSON.stringify(selectedOption)
      )
    )
  }

  /**
   * This method will set the loading in the indicated state
   * @param {string} key
   * @param {boolean} loading
   * @private
   */
  private setLoading(key: string, loading: boolean): void {
    this.setDataFromKey(key, 'loading', loading)
  }

  /**
   * This method will set the error in the indicated state
   * @param {string} key
   * @param {Error | null} error
   * @private
   */
  private setError(key: string, error: Error | null): void {
    this.setDataFromKey(key, 'error', error)
  }

  /**
   * Will set the data in the state from the provided information
   * @param {string} key the name of the impacted state
   * @param {string} dataName the name of the state's attribute to modify
   * @param data the data to set
   * @private
   */
  private setDataFromKey(key: string, dataName: string, data: any): void {
    const state = this.getState()
    this.patchState({
      ...state,
      [key]: {
        ...state[key],
        [dataName]: data,
      },
    })
  }
}
